import type {
  AbsoluteWorkspacePath,
  IPitcherClient,
  RelativeWorkspacePath,
} from "@codesandbox/pitcher-client";
import { FS_SCHEME, relativePathToResource } from "environment-interface";
import {
  GIT_SCHEME,
  createMonacoGitUri,
  parseMonacoGitUri,
} from "environment-interface/monacoEditor/browser/utils/uri-utils";
import { type TabParams } from "features/types/tabs";
import { Selection } from "monaco-editor";
import { Uri } from "vscode";
import type { EditorInput, IUntypedEditorInput } from "vscode/services";

/**
 * Takes a CodeSandbox tab and returns the editor input that should be opened in VSCode
 */
export function createEditorInputFromTab(
  tab: TabParams,
): IUntypedEditorInput | undefined {
  if (tab.type === "DIFF") {
    const resource = relativePathToResource(tab.filepath);

    return {
      original: {
        resource: createMonacoGitUri(tab.filepath, tab.base),
      },
      modified: {
        resource,
      },
    };
  } else if (tab.type === "FILE") {
    const resource = relativePathToResource(tab.filepath);

    const initialSelection = tab.initialSelections && tab.initialSelections[0];

    return {
      resource,
      options: {
        preserveFocus: true,
        selection:
          initialSelection &&
          new Selection(
            initialSelection.startLineNumber,
            initialSelection.startColumn,
            initialSelection.endLineNumber,
            initialSelection.endColumn,
          ),
      },
    };
  } else {
    const resource = Uri.from({
      scheme: "csb-tab",
      query: `tab=${JSON.stringify(tab)}`,
    });

    return {
      resource,
    };
  }
}

export function parseTabURI(uri?: Uri): TabParams | undefined {
  if (!uri) return undefined;
  const tab = JSON.parse(uri.query.replace("tab=", "")) as TabParams;
  return tab;
}

export function createTabFromEditorInput(
  input: EditorInput,
  pitcher: IPitcherClient,
): TabParams | undefined {
  const resource = input.resource;

  if (!resource) {
    return;
  }

  if (resource.scheme === FS_SCHEME) {
    const relativePath = pitcher.clients.fs.absoluteToRelativeWorkspacePath(
      resource.path as AbsoluteWorkspacePath,
    );

    return {
      type: "FILE",
      filepath: relativePath,
    };
  }

  const untypedEditor = input.toUntyped();
  if (!untypedEditor) {
    return;
  }

  if ("original" in untypedEditor) {
    // Diff editor
    const original = untypedEditor.original.resource;
    const modified = untypedEditor.modified.resource;

    if (!original || !modified) {
      return;
    }

    if (original.scheme !== GIT_SCHEME) {
      return;
    }

    const { filepath, gitReference } = parseMonacoGitUri(original);

    return {
      type: "DIFF",
      filepath: filepath as RelativeWorkspacePath,
      base: gitReference,
    };
  }

  if (resource.scheme === "csb-tab") {
    return parseTabURI(resource);
  }
}
